/**
 * Recursively removes empty, null, or undefined values from an object
 * @param {Object} obj - Object to clean
 * @returns {Object} Cleaned object
 */
const removeEmptyValues = (obj) => {
    if (obj === null || obj === undefined) {
        return undefined;
    }

    if (Array.isArray(obj)) {
        const filtered = obj.filter((item) => item !== null && item !== undefined);
        return filtered.length > 0 ? filtered : undefined;
    }

    if (typeof obj === 'object') {
        const cleaned = {};

        Object.keys(obj).forEach((key) => {
            const value = removeEmptyValues(obj[key]);
            if (value !== undefined) {
                cleaned[key] = value;
            }
        });

        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    return obj;
};

/**
 * Optimizes listing data by removing unnecessary fields and keeping only
 * essential information needed for vehicle summaries.
 *
 * This function reduces token count significantly by:
 * - Removing verbose dealer information
 * - Simplifying image arrays
 * - Removing payment calculation URLs
 * - Keeping only essential vehicle specifications
 *
 * @param {Object} listingData - Full listing data object
 * @returns {Object} Optimized listing data with only essential fields
 */
export const optimizeListingData = (listingData) => {
    if (!listingData) {
        return null;
    }

    try {
        // Extract essential vehicle information only
        const optimized = {
            // Basic vehicle info
            id: listingData.id,
            make: listingData.make?.name,
            model: listingData.model?.name,
            year: listingData.year,
            title: listingData.title,
            listingType: listingData.listingType,

            // Key specifications
            mileage: listingData.mileage?.value,
            engine: listingData.engine?.name,
            transmission: listingData.transmission?.description,
            driveType: listingData.driveType?.description,
            fuelType: listingData.fuelType?.name,
            mpgCity: listingData.mpgCity,
            mpgHighway: listingData.mpgHighway,

            // Colors and style
            exteriorColor: listingData.color?.exteriorColor,
            interiorColor: listingData.color?.interiorColor,
            bodyStyle: listingData.bodyStyleCodes?.[0],

            // Pricing (simplified)
            pricing: {
                msrp: listingData.pricingDetail?.msrp,
                salePrice: listingData.pricingDetail?.salePrice,
                incentive: listingData.pricingDetail?.incentive,
            },

            // Essential warranties (simplified)
            warranties: {
                basic: {
                    years: listingData.warranties?.originalWarranties?.basic?.years,
                    miles: listingData.warranties?.originalWarranties?.basic?.miles,
                    details: listingData.warranties?.originalWarranties?.basic?.details,
                },
                corrosion: {
                    years: listingData.warranties?.originalWarranties?.corrosion?.years,
                    miles: listingData.warranties?.originalWarranties?.corrosion?.miles,
                    details: listingData.warranties?.originalWarranties?.corrosion?.details,
                },
                roadside: {
                    years: listingData.warranties?.originalWarranties?.roadsideAssistance?.years,
                    miles: listingData.warranties?.originalWarranties?.roadsideAssistance?.miles,
                    details: listingData.warranties?.originalWarranties?.roadsideAssistance?.details,
                },
            },

            // Vehicle condition indicators
            vhrPreview: listingData.vhrPreview,
            hasLeatherSeats: listingData.hasLeatherSeats,

            // Minimal owner info (just essentials)
            owner: {
                name: listingData.owner?.name,
                city: listingData.owner?.location?.address?.city,
                state: listingData.owner?.location?.address?.state,
                rating: listingData.owner?.rating?.value,
            },

            // Stock and VIN for reference
            stockNumber: listingData.stockNumber,
            vin: listingData.vin,

            // Lifestyle categories for context
            lifestyleCategories: listingData.lifestyleCategories,
        };
        // Remove any undefined or null values to further reduce size
        return removeEmptyValues(optimized);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[vehicleSummaryUtils] - optimizeListingData failed: ${error}`);
    }

    return listingData;
};

/**
 * Creates a minimal vehicle summary object with only the most essential fields
 * for basic vehicle summaries where even more aggressive optimization is needed.
 *
 * @param {Object} listingData - Full listing data object
 * @returns {Object} Minimal vehicle data
 */
export const createMinimalVehicleData = (listingData) => {
    if (!listingData) {
        return null;
    }

    return {
        make: listingData.make?.name,
        model: listingData.model?.name,
        year: listingData.year,
        mileage: listingData.mileage?.value,
        price: listingData.pricingDetail?.salePrice || listingData.pricingDetail?.msrp,
        engine: listingData.engine?.name,
        transmission: listingData.transmission?.description,
        driveType: listingData.driveType?.description,
        mpgCity: listingData.mpgCity,
        mpgHighway: listingData.mpgHighway,
        exteriorColor: listingData.color?.exteriorColor,
        interiorColor: listingData.color?.interiorColor,
        basicWarranty: listingData.warranties?.originalWarranties?.basic?.details,
        corrosionWarranty: listingData.warranties?.originalWarranties?.corrosion?.details,
    };
};

/**
 * Optimizes vehicle features by extracting only the most relevant features
 * for creating vehicle summaries. This reduces token count while preserving
 * key information that customers care about most.
 *
 * @param {Object} vehicleFeatures - Full vehicle features object
 * @returns {Object} Optimized vehicle features with only essential information
 */
export const optimizeVehicleFeatures = (vehicleFeatures) => {
    if (!vehicleFeatures) {
        return null;
    }

    try {
        const optimized = {};

        // Helper function to extract features by label
        const extractFeaturesByLabel = (features, relevantLabels) => {
            if (!features || !Array.isArray(features)) return {};

            const result = {};
            features
                .filter((feature) => {
                    const label = feature.label?.toLowerCase();
                    return relevantLabels.some((relevantLabel) => label?.includes(relevantLabel.toLowerCase()))
                        && feature.value !== 'Information Unavailable';
                })
                .forEach((feature) => {
                    if (feature.label && feature.value) {
                        result[feature.label] = feature.value;
                    }
                });

            return result;
        };

        // Process each category with relevant features only
        Object.keys(vehicleFeatures).forEach((category) => {
            const features = vehicleFeatures[category];
            let relevantFeatures = {};

            switch (category) {
                case 'Safety Features':
                case 'Accident Prevention':
                    relevantFeatures = extractFeaturesByLabel(features, [
                        'Rearview Camera', 'Parking assist', 'Stability Control',
                        'Traction Control', 'Brake Assist', 'Bluetooth',
                        'HID Headlights', 'Daytime Running Lights',
                    ]);
                    break;

                case 'Comfort':
                    relevantFeatures = extractFeaturesByLabel(features, [
                        'Air Conditioning', 'Climate Control', 'Leather Seats',
                        'Heated Driver Seat', 'Panoramic Roof', 'Sunroof',
                        'Dual Zone Climate Control',
                    ]);
                    break;

                case 'Convenience':
                    relevantFeatures = extractFeaturesByLabel(features, [
                        'Navigation System', 'Keyless Start', 'Power Liftgate',
                        'Cruise Control', 'Power Driver Seat', 'Power Passenger Seat',
                        'Roof Rack', 'Running Boards',
                    ]);
                    break;

                case 'Entertainment':
                    relevantFeatures = extractFeaturesByLabel(features, [
                        'Premium Sound System', 'Satellite Radio', 'Entertainment System',
                        'CD Player', 'MP3 Player',
                    ]);
                    break;

                case 'Package':
                default:
                    // For other categories, take a more conservative approach
                    if (Array.isArray(features)) {
                        const limitedFeatures = features
                            .filter((feature) => feature.value !== 'Information Unavailable')
                            .slice(0, 5); // Limit to top 5 features

                        limitedFeatures.forEach((feature) => {
                            if (feature.label && feature.value) {
                                relevantFeatures[feature.label] = feature.value;
                            }
                        });
                    }
                    break;
            }

            // Only add category if it has relevant features
            if (Object.keys(relevantFeatures).length > 0) {
                optimized[category] = relevantFeatures;
            }
        });

        // Remove any undefined or null values to further reduce size
        return removeEmptyValues(optimized);
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[vehicleSummaryUtils] - optimizeVehicleFeatures failed: ${error}`);
        return vehicleFeatures;
    }
};

/**
 * Estimates token reduction achieved by the optimization
 * @param {Object} originalData - Original listing data
 * @param {Object} optimizedData - Optimized listing data
 * @returns {Object} Token reduction statistics
 */
export const estimateTokenReduction = (originalData, optimizedData) => {
    const originalSize = JSON.stringify(originalData).length;
    const optimizedSize = JSON.stringify(optimizedData).length;
    const reduction = originalSize - optimizedSize;
    const reductionPercentage = Math.round((reduction / originalSize) * 100);

    return {
        originalSize,
        optimizedSize,
        reduction,
        reductionPercentage,
        estimatedTokenReduction: Math.round(reduction / 4), // Rough estimate: 4 chars per token
    };
};
